import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Menu, Transition } from "@headlessui/react";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  CodeBracketIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  PaperAirplaneIcon,
  PencilIcon,
  PlusIcon,
  PrinterIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  createMeetingMinutesEntry,
  createMeetingMinutesGroup,
  deleteMeetingMinutesEntry,
  deleteMeetingMinutesGroup,
  fetchMeetingMinutesById,
  fetchMeetingMinutesMeeting,
  fetchMeetingMinutesMeetingPdf,
  saveMeetingMinutesMeetingAsTemplate,
  sendMeetingMinutesMeeting,
  updateMeetingMinutesGroup,
  updateMeetingMinutesMeeting,
} from "../../../api/meetingMinutes";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import { SaveBar } from "../../../components/utils/SaveBar";
import { PMDialog } from "../../../components/utils/PMDialog";
import { AIGenerator } from "../../../components/utils/AIGenerator";
import { aiGeneratorConfigs } from "../../../config/aiGeneratorConfigs";
import { MeetingMinutesJsonDialog } from "../../../components/dashboard/meeting-minutes/MeetingMinutesJsonDialog";
import { parseImportedMeetingMinutesJson } from "../../../components/dashboard/meeting-minutes/meetingMinutesImportUtils";
import { persistParsedAgendaToMeeting } from "../../../components/dashboard/meeting-minutes/persistMeetingMinutesAgenda";
import { MeetingEntryEditor } from "./MeetingEntryEditor";
import { indexMeetingEntries, mergeMeetingAgenda } from "./meetingAgendaMerge";
import { RecordCollaborationPanel } from "../../../collaboration/RecordCollaborationPanel";
import DocumentationSendDialog from "../../../components/dashboard/shared/DocumentationSendDialog";
import DocumentationExportDialog from "../../../components/dashboard/shared/DocumentationExportDialog";
import SendLogPanel from "../../../components/dashboard/shared/SendLogPanel";
import HistoriesModal from "../../../components/dashboard/histories/modal";
import { DocumentManagerV6 } from "../../../components/dashboard/Media/DocumentManagerV6";
import { PromangRichTextContent } from "../../../components/utils/PromangRichTextContent";
import {
  MeetingMinutesShell,
  mmCardClass,
  mmPrimaryButtonClass,
  mmSecondaryButtonClass,
} from "./MeetingMinutesShell";

export const MeetingMinutesMeetingView = () => {
  const { t } = useTranslation();
  const { id, meetingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [meeting, setMeeting] = useState(null);
  const [meetingMinutes, setMeetingMinutes] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [saveErrorMessage, setSaveErrorMessage] = useState(null);
  const [documentRefreshTrigger, setDocumentRefreshTrigger] = useState(0);
  const [isEditMode, setIsEditMode] = useState(true);
  const [previousMeeting, setPreviousMeeting] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupFormData, setGroupFormData] = useState({ title: "", description: "" });
  const [sendOpen, setSendOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateTitle, setTemplateTitle] = useState("");
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [finishConfirmOpen, setFinishConfirmOpen] = useState(false);
  const [markingFinished, setMarkingFinished] = useState(false);
  const meetingRef = useRef(null);
  const scrollPositionRef = useRef(null);
  const editVersionRef = useRef(null);
  const draftKeysRef = useRef(new Set());
  const focusKeysRef = useRef(new Set());
  const freshEntriesRef = useRef(new Map());
  const [collabGeneration, setCollabGeneration] = useState(0);
  const entryFlushersRef = useRef(new Set());
  const incomingVersion = meeting?.edit_version;
  if (incomingVersion && (!editVersionRef.current || incomingVersion > editVersionRef.current)) {
    editVersionRef.current = incomingVersion;
  }

  const withEditVersion = (payload = {}) => (
    editVersionRef.current ? { ...payload, edit_version: editVersionRef.current } : payload
  );

  const adoptEditVersion = (record, version) => {
    if (!record || !version) return record;
    return { ...record, edit_version: version };
  };

  const staleOr = (err, fallbackKey) => {
    if (err?.response?.status === 409) {
      return err.response.data?.problem?.message
        || (Array.isArray(err.response.data?.errors) ? err.response.data.errors.filter(Boolean).join(", ") : null)
        || t("meeting_minutes.errors.stale");
    }
    return t(fallbackKey);
  };

  const applyParsedToMeeting = useCallback(
    async (parsed, mode = "append", { notify = true } = {}) => {
      setSaving(true);
      try {
        const current = meetingRef.current || meeting;
        const createdGroups = await persistParsedAgendaToMeeting({
          meetingMinutesId: id,
          meetingId,
          parsed,
          mode,
          existingGroups: current?.groups || [],
          editVersion: editVersionRef.current || current?.edit_version,
          createGroup: createMeetingMinutesGroup,
          createEntry: createMeetingMinutesEntry,
          deleteGroup: deleteMeetingMinutesGroup,
        });
        setMeeting((prevMeeting) => {
          const baseGroups = mode === "replace" ? [] : prevMeeting.groups || [];
          const version = [...createdGroups].reverse().reduce((found, group) => {
          if (found) return found;
          const entry = [...(group.entries || [])].reverse().find((row) => row?.edit_version);
          return entry?.edit_version || group.edit_version || null;
        }, null);
        const updated = adoptEditVersion({
            ...prevMeeting,
            groups: [...baseGroups, ...createdGroups],
          }, version);
          meetingRef.current = updated;
          return updated;
        });
        if (notify) {
          toast.success(
            t("meeting_minutes.json.ai_apply_success", { count: parsed.groups.length })
          );
        }
      } catch (err) {
        if (err?.response?.status === 409) {
          toast.error(staleOr(err, "meeting_minutes.errors.stale"));
          throw err;
        }
        const code = err?.code || "unknown";
        const key = `meeting_minutes.json.error_${code}`;
        const msg = t(key);
        toast.error(msg !== key ? msg : t("meeting_minutes.json.ai_parse_error"));
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [id, meetingId, meeting, t]
  );

  const handleEntryDocumentsChange = useCallback(() => {
    setDocumentRefreshTrigger((prev) => prev + 1);
  }, []);

  const handleSaveStatus = useCallback((status, errorMessage = null) => {
    if (status === "saving") {
      setSaveStatus("saving");
      return;
    }
    if (status === "success") {
      setSaveStatus("success");
      setTimeout(() => setSaveStatus(null), 3000);
      return;
    }
    if (status === "error") {
      setSaveStatus("error");
      setSaveErrorMessage(errorMessage);
      setTimeout(() => {
        setSaveStatus(null);
        setSaveErrorMessage(null);
      }, 5000);
      if (errorMessage) toast.error(errorMessage);
    }
  }, []);

  const patchEntryLocal = useCallback((groupId, entryId, patch) => {
    setMeeting((prevMeeting) => {
      const updated = {
        ...prevMeeting,
        groups: prevMeeting.groups.map((group) =>
          group.id === groupId
            ? {
                ...group,
                entries: group.entries.map((entry) =>
                  entry.id === entryId ? { ...entry, ...patch } : entry
                ),
              }
            : group
        ),
      };
      meetingRef.current = updated;
      return updated;
    });
  }, []);

  const handleDraftChange = useCallback((entryId, field, dirty) => {
    const key = `${entryId}:${field}`;
    if (dirty) draftKeysRef.current.add(key);
    else draftKeysRef.current.delete(key);
    setHasUnsavedChanges(draftKeysRef.current.size > 0);
  }, []);

  const handleFieldFocus = useCallback((entryId, field, focused) => {
    const key = `${entryId}:${field}`;
    if (focused) focusKeysRef.current.add(key);
    else focusKeysRef.current.delete(key);
  }, []);

  const acceptRemoteField = useCallback((entryId, field) => {
    if (draftKeysRef.current.has(`${entryId}:${field}`)) return;
    const remote = freshEntriesRef.current.get(entryId);
    if (!remote) return;
    setMeeting((prev) => {
      if (!prev?.groups) return prev;
      const updated = {
        ...prev,
        groups: prev.groups.map((group) => ({
          ...group,
          entries: (group.entries || []).map((entry) => {
            if (entry.id !== entryId) return entry;
            if (field === "description") return { ...entry, description: remote.description };
            if (field === "deadline") return { ...entry, deadline: remote.deadline };
            if (field === "responsible_user_id") {
              return {
                ...entry,
                responsible_user_id: remote.responsible_user_id,
                responsible_user: remote.responsible_user,
              };
            }
            return entry;
          }),
        })),
      };
      meetingRef.current = updated;
      return updated;
    });
  }, []);

  const registerEntryFlush = useCallback((flush) => {
    entryFlushersRef.current.add(flush);
    return () => entryFlushersRef.current.delete(flush);
  }, []);

  const flushEntryDrafts = useCallback(async () => {
    const jobs = [...entryFlushersRef.current].map((flush) => flush());
    await Promise.all(jobs);
  }, []);

  const handleEntryPersisted = useCallback((groupId, entryId, updatedEntry, meta = {}) => {
    if (updatedEntry?.edit_version && (!editVersionRef.current || updatedEntry.edit_version > editVersionRef.current)) {
      editVersionRef.current = updatedEntry.edit_version;
    }
    setMeeting((prevMeeting) => {
      const updated = {
        ...prevMeeting,
        groups: prevMeeting.groups.map((group) =>
          group.id === groupId
            ? {
                ...group,
                entries: group.entries.map((entry) => {
                  if (entry.id !== entryId) return entry;
                  return {
                    ...entry,
                    ...updatedEntry,
                    description: entry.description,
                    deadline: meta.field === "deadline" ? updatedEntry.deadline : entry.deadline,
                    responsible_user_id:
                      meta.field === "responsible_user_id"
                        ? updatedEntry.responsible_user_id
                        : entry.responsible_user_id,
                    responsible_user:
                      meta.field === "responsible_user_id"
                        ? updatedEntry.responsible_user
                        : entry.responsible_user,
                  };
                }),
              }
            : group
        ),
      };
      meetingRef.current = adoptEditVersion(updated, updatedEntry?.edit_version);
      return meetingRef.current;
    });
    const remote = freshEntriesRef.current.get(entryId);
    if (remote && meta.field) {
      const next = { ...remote };
      if (meta.field === "description") next.description = updatedEntry.description;
      if (meta.field === "deadline") next.deadline = updatedEntry.deadline;
      if (meta.field === "responsible_user_id") {
        next.responsible_user_id = updatedEntry.responsible_user_id;
        next.responsible_user = updatedEntry.responsible_user;
      }
      freshEntriesRef.current.set(entryId, next);
    }
  }, []);

  const suggestedRecipients = (() => {
    if (meeting?.suggested_recipients?.length) {
      return meeting.suggested_recipients
        .filter((recipient) => recipient?.email)
        .map((recipient) => ({
          key: recipient.key || recipient.email,
          label: recipient.label || recipient.name,
          name: recipient.name,
          email: recipient.email,
          // Keep channel/preselect so private-email suggestions stay opt-in in the picker.
          group: recipient.group,
          channel: recipient.channel,
          preselect: recipient.preselect,
        }));
    }
    return [];
  })();

  const handleMeetingSent = (response) => {
    if (!response) return;
    setMeeting((prev) => ({
      ...prev,
      ...response,
      last_sent_at: response.last_sent_at,
      send_log: response.send_log,
      suggested_recipients: response.suggested_recipients || prev.suggested_recipients,
      groups: prev.groups,
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const meetingData = await fetchMeetingMinutesMeeting(id, meetingId);
        setMeeting(meetingData);
        meetingRef.current = meetingData;
        setIsFinished(meetingData.is_finished === true);
        if (meetingData.is_finished === true) setIsEditMode(false);

        const minutesData = await fetchMeetingMinutesById(id);
        setMeetingMinutes(minutesData);
        setTemplateTitle(minutesData?.title || "");

        if (meetingData.meeting_number && meetingData.meeting_number > 1) {
          const previousMeetingNumber = meetingData.meeting_number - 1;
          const previousMeetingData = minutesData.meetings?.find(
            (m) => m.meeting_number === previousMeetingNumber
          );
          if (previousMeetingData) {
            const prevMeeting = await fetchMeetingMinutesMeeting(id, previousMeetingData.id);
            setPreviousMeeting(prevMeeting);
          }
        }
      } catch (err) {
        setError(true);
        toast.error(t("meeting_minutes.meeting.load_error"));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, meetingId, t]);

  const openGroupDialog = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setGroupFormData({ title: group.title || "", description: group.description || "" });
    } else {
      setEditingGroup(null);
      setGroupFormData({ title: "", description: "" });
    }
    setGroupDialogOpen(true);
  };

  const closeGroupDialog = () => {
    setGroupDialogOpen(false);
    setEditingGroup(null);
    setGroupFormData({ title: "", description: "" });
  };

  const saveGroup = async () => {
    try {
      setSaving(true);
      if (editingGroup) {
        const updatedGroup = await updateMeetingMinutesGroup(id, meetingId, editingGroup.id, withEditVersion({
          title: groupFormData.title,
          description: groupFormData.description,
        }));
        setMeeting((prevMeeting) => {
          const updated = adoptEditVersion({
            ...prevMeeting,
            groups: prevMeeting.groups.map((g) =>
              g.id === editingGroup.id
                ? {
                    ...g,
                    title: updatedGroup.title,
                    description: updatedGroup.description,
                  }
                : g
            ),
          }, updatedGroup.edit_version);
          meetingRef.current = updated;
          return updated;
        });
        toast.success(t("meeting_minutes.group.updated"));
      } else {
        const currentMeeting = meetingRef.current || meeting;
        const newGroup = await createMeetingMinutesGroup(id, meetingId, withEditVersion({
          title: groupFormData.title,
          description: groupFormData.description,
          order: currentMeeting?.groups?.length || 0,
        }));
        setMeeting((prevMeeting) => {
          const updated = adoptEditVersion({
            ...prevMeeting,
            groups: [...(prevMeeting.groups || []), { ...newGroup, entries: newGroup.entries || [] }],
          }, newGroup.edit_version);
          meetingRef.current = updated;
          return updated;
        });
        toast.success(t("meeting_minutes.group.created"));
      }
      closeGroupDialog();
    } catch (err) {
      toast.error(
        staleOr(
          err,
          editingGroup ? "meeting_minutes.point.update_error" : "meeting_minutes.point.create_error"
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const removeGroup = async (groupId) => {
    if (!window.confirm(t("meeting_minutes.group.delete_confirm"))) return;
    try {
      const removed = await deleteMeetingMinutesGroup(id, meetingId, groupId, editVersionRef.current);
      setMeeting((prevMeeting) => {
        const updated = adoptEditVersion({
          ...prevMeeting,
          groups: prevMeeting.groups.filter((g) => g.id !== groupId),
        }, removed?.edit_version);
        meetingRef.current = updated;
        return updated;
      });
      toast.success(t("meeting_minutes.group.deleted"));
    } catch (err) {
      toast.error(staleOr(err, "meeting_minutes.group.delete_error"));
    }
  };

  const addEntry = async (e, groupId) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    scrollPositionRef.current = window.scrollY || 0;
    try {
      setSaving(true);
      const currentMeeting = meetingRef.current || meeting;
      const group = currentMeeting?.groups?.find((g) => g.id === groupId);
      const newEntry = await createMeetingMinutesEntry(id, meetingId, groupId, withEditVersion({
        description: "",
        deadline: "",
        responsible_user_id: null,
        order: group?.entries?.length || 0,
      }));
      setMeeting((prevMeeting) => {
        const updated = adoptEditVersion({
          ...prevMeeting,
          groups: prevMeeting.groups.map((g) =>
            g.id === groupId ? { ...g, entries: [...(g.entries || []), newEntry] } : g
          ),
        }, newEntry?.edit_version);
        meetingRef.current = updated;
        return updated;
      });
    } catch (err) {
      toast.error(staleOr(err, "meeting_minutes.entry.create_error"));
      scrollPositionRef.current = null;
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (scrollPositionRef.current !== null) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollPositionRef.current, left: 0, behavior: "instant" });
        scrollPositionRef.current = null;
      });
    }
  }, [meeting?.groups]);

  const removeEntry = async (groupId, entryId) => {
    if (!window.confirm(t("meeting_minutes.entry.delete_confirm"))) return;
    try {
      const removed = await deleteMeetingMinutesEntry(id, meetingId, groupId, entryId, editVersionRef.current);
      setMeeting((prevMeeting) => {
        const updated = adoptEditVersion({
          ...prevMeeting,
          groups: prevMeeting.groups.map((g) =>
            g.id === groupId ? { ...g, entries: g.entries.filter((e) => e.id !== entryId) } : g
          ),
        }, removed?.edit_version);
        meetingRef.current = updated;
        return updated;
      });
      toast.success(t("meeting_minutes.entry.deleted"));
    } catch (err) {
      toast.error(staleOr(err, "meeting_minutes.entry.delete_error"));
    }
  };

  const handleSaveAsTemplate = async () => {
    try {
      const result = await saveMeetingMinutesMeetingAsTemplate(id, meetingId, {
        title: templateTitle,
      });
      if (result?.errors) throw new Error(result.errors.join?.(", "));
      toast.success(t("meeting_minutes.templates.saved_from_meeting"));
      setSaveTemplateOpen(false);
    } catch (err) {
      toast.error(t("meeting_minutes.templates.save_from_meeting_error"));
    }
  };

  const confirmMarkFinished = async () => {
    if (markingFinished) return;
    try {
      setMarkingFinished(true);
      await flushEntryDrafts();
      if (draftKeysRef.current.size > 0) {
        toast.error(t("meeting_minutes.meeting.mark_finished_error"));
        return;
      }
      const saved = await updateMeetingMinutesMeeting(id, meetingId, withEditVersion({ is_finished: true }));
      setIsFinished(true);
      setIsEditMode(false);
      setMeeting((prev) => {
        const updated = adoptEditVersion({ ...prev, is_finished: true }, saved?.edit_version);
        meetingRef.current = updated;
        return updated;
      });
      setFinishConfirmOpen(false);
      toast.success(t("meeting_minutes.meeting.marked_finished"));
    } catch (err) {
      toast.error(staleOr(err, "meeting_minutes.meeting.mark_finished_error"));
    } finally {
      setMarkingFinished(false);
    }
  };

  if (loading) return <ResourceLoading />;
  if (error || !meeting) return <ResourceError />;

  return (
    <div className="min-h-screen bg-slate-50">
      <RecordCollaborationPanel
        record={meeting}
        recordType="meeting_minutes_meeting"
        section="agenda"
        editing={hasUnsavedChanges || saving || groupDialogOpen}
        refreshBlocked={groupDialogOpen}
        saving={saving || saveStatus === "saving"}
        onRefresh={async () => {
          const fresh = await fetchMeetingMinutesMeeting(id, meetingId);
          if (!fresh?.id || !fresh.edit_version) throw fresh;
          return {
            apply: () => {
              freshEntriesRef.current = indexMeetingEntries(fresh);
              const protectedKeys = new Set([...draftKeysRef.current, ...focusKeysRef.current]);
              setMeeting((prev) => {
                const merged = mergeMeetingAgenda(prev, fresh, protectedKeys);
                meetingRef.current = merged;
                return merged;
              });
              setIsFinished(fresh.is_finished === true);
              if (fresh.is_finished === true) setIsEditMode(false);
              setCollabGeneration((n) => n + 1);
            },
          };
        }}
      />
      <SaveBar
        hasChanges={hasUnsavedChanges}
        isSaving={saveStatus === "saving"}
        isError={saveStatus === "error"}
        isSuccess={saveStatus === "success"}
        errorMessage={saveErrorMessage}
        successMessage={t("meeting_minutes.changes_saved")}
        onDismiss={() => {
          setSaveStatus(null);
          setSaveErrorMessage(null);
        }}
      />

      <MeetingMinutesShell
        title={`${t("meeting_minutes.meeting_number")} ${meeting.meeting_number} – ${
          meetingMinutes?.title || ""
        }`}
        subtitle={
          [
            meeting.meeting_date,
            meetingMinutes?.project
              ? meetingMinutes.project.displayTitle || meetingMinutes.project.name
              : null,
          ]
            .filter(Boolean)
            .join(" · ")
        }
        backTo={`/dashboard/meeting-minutes/${id}`}
        backLabel={t("common.back")}
        actions={
          <>
            <button type="button" onClick={() => setSendOpen(true)} className={mmPrimaryButtonClass()}>
              <PaperAirplaneIcon className="h-4 w-4" />
              {t("meeting_minutes.send")}
            </button>
            <Menu as="div" className="relative inline-block text-left">
              <Menu.Button
                type="button"
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-[#dfe5ec] bg-white px-3.5 text-[13px] font-semibold text-[#33445a] hover:bg-[#f4f7fa]"
              >
                {t("meeting_minutes.actions.open")}
                <ChevronDownIcon className="h-4 w-4 text-[#8b98a8]" aria-hidden />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-30 mt-2 w-56 origin-top-right rounded-[11px] border border-[#dfe5ec] bg-white p-1.5 shadow-[0_12px_32px_rgba(20,35,55,.14)] focus:outline-none">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        type="button"
                        className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm text-[#33445a] ${
                          active ? "bg-[#f2f5f9]" : ""
                        }`}
                        onClick={() => setExportOpen(true)}
                      >
                        <PrinterIcon className="h-4 w-4 text-[#5b6b7f]" aria-hidden />
                        {t("meeting_minutes.export_pdf")}
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        type="button"
                        className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm text-[#33445a] ${
                          active ? "bg-[#f2f5f9]" : ""
                        }`}
                        onClick={() => setSaveTemplateOpen(true)}
                      >
                        <DocumentDuplicateIcon className="h-4 w-4 text-[#5b6b7f]" aria-hidden />
                        {t("meeting_minutes.templates.save_from_meeting")}
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        type="button"
                        className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm text-[#33445a] ${
                          active ? "bg-[#f2f5f9]" : ""
                        }`}
                        onClick={() => setHistoryOpen(true)}
                      >
                        <ClockIcon className="h-4 w-4 text-[#5b6b7f]" aria-hidden />
                        {t("histories.title")}
                      </button>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Transition>
            </Menu>
            {!isFinished ? (
              <button
                type="button"
                onClick={() => {
                  if (isEditMode) {
                    void flushEntryDrafts().finally(() => setIsEditMode(false));
                    return;
                  }
                  setIsEditMode(true);
                }}
                className={mmSecondaryButtonClass()}
              >
                {isEditMode ? <EyeIcon className="h-4 w-4" /> : <PencilIcon className="h-4 w-4" />}
                {isEditMode ? t("meeting_minutes.view_mode") : t("meeting_minutes.edit_mode")}
              </button>
            ) : null}
          </>
        }
      >
        <div className={`${mmCardClass()} px-6 py-6`}>
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            {meeting.last_sent_at ? (
              <p className="text-sm font-medium text-emerald-700">
                {t("documentationSend.sent_badge", {
                  date: new Date(meeting.last_sent_at).toLocaleString(),
                })}
              </p>
            ) : (
              <p className="text-sm text-slate-500">{t("documentationSend.not_sent_yet")}</p>
            )}
            {!isFinished ? (
              <button
                type="button"
                onClick={() => setFinishConfirmOpen(true)}
                className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
              >
                <CheckIcon className="h-4 w-4" />
                {t("meeting_minutes.meeting.mark_finished")}
              </button>
            ) : (
              <span className="rounded-full bg-emerald-100 px-3 py-1.5 text-sm font-semibold text-emerald-800">
                {t("meeting_minutes.meeting.finished")}
              </span>
            )}
          </div>

          {!isFinished && isEditMode ? (
            <div className="mb-6 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => openGroupDialog()}
                className={mmSecondaryButtonClass()}
              >
                <PlusIcon className="h-4 w-4" />
                {t("meeting_minutes.add_point")}
              </button>
              <button
                type="button"
                onClick={() => setJsonDialogOpen(true)}
                className={mmSecondaryButtonClass()}
              >
                <CodeBracketIcon className="h-4 w-4" />
                {t("meeting_minutes.json.open_dialog")}
              </button>
              <AIGenerator
                module="meeting_minutes"
                context={{
                  minutes_title: meetingMinutes?.title || "",
                  minutes_description: meetingMinutes?.description || "",
                  meeting_type: meetingMinutes?.meeting_type || "",
                  project_name:
                    meetingMinutes?.project?.displayTitle ||
                    meetingMinutes?.project?.name ||
                    "",
                  employee_name:
                    meetingMinutes?.employee?.displayTitle ||
                    meetingMinutes?.employee?.name ||
                    "",
                }}
                fieldsToGenerate={["agenda_json"]}
                onGenerate={async (generatedFields, meta) => {
                  const raw = generatedFields?.agenda_json;
                  if (raw == null || typeof raw !== "string" || !String(raw).trim()) {
                    toast.error(t("meeting_minutes.json.ai_parse_error"));
                    return;
                  }
                  try {
                    const parsed = parseImportedMeetingMinutesJson(raw);
                    await applyParsedToMeeting(
                      parsed,
                      meta?.generationOptions?.apply_mode || "append"
                    );
                  } catch (err) {
                    if (err?.code === "create_group_failed" || err?.code === "create_entry_failed") {
                      return;
                    }
                    const code = err?.code || "unknown";
                    const key = `meeting_minutes.json.error_${code}`;
                    const msg = t(key);
                    toast.error(
                      msg !== key ? msg : t("meeting_minutes.json.ai_parse_error")
                    );
                  }
                }}
                config={{
                  ...aiGeneratorConfigs.meeting_minutes,
                  options: {
                    ...aiGeneratorConfigs.meeting_minutes.options,
                    apply_mode: {
                      ...aiGeneratorConfigs.meeting_minutes.options.apply_mode,
                      default: "append",
                    },
                  },
                }}
                buttonLabel={t("meeting_minutes.json.generate_with_ai")}
                className="!rounded-full !border-slate-300 !px-4 !py-2 !text-sm !font-semibold"
              />
            </div>
          ) : null}

          <div className="space-y-5">
            {meeting.groups?.length ? (
              meeting.groups.map((group) => (
                <div
                  key={group.id}
                  data-group-id={group.id}
                  className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 sm:p-5"
                >
                  <div className="mb-3 flex items-start justify-between gap-2">
                    <div>
                      <h4 className="text-lg font-semibold text-slate-950">
                        {group.title || t("meeting_minutes.point.untitled")}
                      </h4>
                      {group.description ? (
                        <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">
                          {group.description}
                        </p>
                      ) : null}
                    </div>
                    {!isFinished && isEditMode ? (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => openGroupDialog(group)}
                          className="rounded-full p-2 text-[#1463C7] hover:bg-white"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeGroup(group.id)}
                          className="rounded-full p-2 text-red-600 hover:bg-white"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    ) : null}
                  </div>

                  <div className="space-y-3">
                    {group.entries?.map((entry) => (
                      <div key={entry.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                        {isFinished || !isEditMode ? (
                          <div>
                            <PromangRichTextContent
                              html={entry.description || ""}
                              className="text-slate-900"
                            />
                            {(entry.deadline || entry.responsible_user_id) && (
                              <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-600">
                                {entry.deadline ? (
                                  <span>
                                    {t("meeting_minutes.entry.deadline")}: {entry.deadline}
                                  </span>
                                ) : null}
                                {entry.responsible_user_id ? (
                                  <span>
                                    {t("meeting_minutes.entry.responsible")}:{" "}
                                    {entry.responsible_user?.displayTitle || entry.responsible_user_id}
                                  </span>
                                ) : null}
                              </div>
                            )}
                            <div className="mt-3">
                              <DocumentManagerV6
                                module="meeting_minutes"
                                moduleParam={`${id}:group:${group.id}:entry:${entry.id}`}
                                mode="dropzone"
                                showUpload={false}
                                allowDelete={false}
                                allowEdit={false}
                                allowCopy={true}
                                allowMove={false}
                                disabled={true}
                                autoLoad={true}
                                onDocumentsChange={handleEntryDocumentsChange}
                              />
                            </div>
                          </div>
                        ) : (
                          <MeetingEntryEditor
                            entry={entry}
                            groupId={group.id}
                            meetingMinutesId={id}
                            meetingId={meetingId}
                            onPatch={patchEntryLocal}
                            onPersisted={handleEntryPersisted}
                            onDraftChange={handleDraftChange}
                            onFieldFocus={handleFieldFocus}
                            onAcceptRemote={acceptRemoteField}
                            freshEntriesRef={freshEntriesRef}
                            collabGeneration={collabGeneration}
                            onRemove={removeEntry}
                            onDocumentsChange={handleEntryDocumentsChange}
                            onSaveStatus={handleSaveStatus}
                            editVersion={meeting.edit_version}
                            onRegisterFlush={registerEntryFlush}
                          />
                        )}
                      </div>
                    ))}

                    {!isFinished && isEditMode ? (
                      <button
                        type="button"
                        onClick={(e) => addEntry(e, group.id)}
                        className="inline-flex items-center gap-1 text-sm font-semibold text-[#1463C7]"
                      >
                        <PlusIcon className="h-4 w-4" />
                        {t("meeting_minutes.entry.add")}
                      </button>
                    ) : null}

                    {previousMeeting?.groups?.length
                      ? (() => {
                          const currentGroupOrder =
                            group.order !== undefined
                              ? group.order
                              : meeting.groups.findIndex((g) => g.id === group.id);
                          const prevGroup =
                            previousMeeting.groups.find(
                              (g) =>
                                (g.order !== undefined
                                  ? g.order
                                  : previousMeeting.groups.findIndex((pg) => pg.id === g.id)) ===
                                currentGroupOrder
                            ) ||
                            previousMeeting.groups[
                              meeting.groups.findIndex((g) => g.id === group.id)
                            ];
                          const hasPrevEntries = prevGroup?.entries?.length > 0;
                          const accordionKey = `prev-entries-${group.id}`;
                          const isExpanded = expandedGroups[accordionKey] === true;
                          if (!hasPrevEntries) return null;
                          return (
                            <div className="mt-4 border-t border-slate-200 pt-3">
                              <button
                                type="button"
                                onClick={() =>
                                  setExpandedGroups((prev) => ({
                                    ...prev,
                                    [accordionKey]: !prev[accordionKey],
                                  }))
                                }
                                className="flex w-full items-center justify-between rounded-xl px-2 py-2 text-left text-sm text-slate-600 hover:bg-white"
                              >
                                <span>
                                  {t("meeting_minutes.from_previous_meeting")} (
                                  {t("meeting_minutes.meeting_number")} {previousMeeting.meeting_number}
                                  )
                                </span>
                                {isExpanded ? (
                                  <ChevronUpIcon className="h-4 w-4" />
                                ) : (
                                  <ChevronDownIcon className="h-4 w-4" />
                                )}
                              </button>
                              {isExpanded
                                ? prevGroup.entries.map((entry) => (
                                    <div
                                      key={`prev-entry-${entry.id}`}
                                      className="mt-2 rounded-xl border border-slate-200 bg-slate-100 p-3 text-sm text-slate-700"
                                    >
                                      <PromangRichTextContent html={entry.description || ""} />
                                    </div>
                                  ))
                                : null}
                            </div>
                          );
                        })()
                      : null}
                  </div>
                </div>
              ))
            ) : (
              <div className="py-12 text-center text-sm text-slate-500">
                {isFinished
                  ? t("meeting_minutes.meeting.no_points")
                  : t("meeting_minutes.meeting.no_points_add_first")}
              </div>
            )}
          </div>

          <div className="mt-8 border-t border-slate-100 pt-6">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
              {t("documentationSend.history_title")}
            </h3>
            <SendLogPanel
              sendLog={meeting.send_log}
              lastSentAt={meeting.last_sent_at}
              onExportSendLog={() => setExportOpen(true)}
            />
          </div>
        </div>
      </MeetingMinutesShell>

      <MeetingMinutesJsonDialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        title={meetingMinutes?.title || ""}
        description={meetingMinutes?.description || ""}
        meeting_type={meetingMinutes?.meeting_type || "project_meeting"}
        groups={meeting?.groups || []}
        onApply={async (parsed) => {
          await applyParsedToMeeting(parsed, parsed.mode || "append", { notify: false });
        }}
      />

      <DocumentationSendDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        resourceType="meeting_minutes"
        resourceTitle={meeting?.title || meetingMinutes?.title}
        suggestedRecipients={suggestedRecipients}
        allowMultiple
        onSend={(payload) => sendMeetingMinutesMeeting(id, meetingId, payload)}
        onSent={handleMeetingSent}
      />

      <DocumentationExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        resourceTitle={meeting?.title || meetingMinutes?.title}
        sendLog={meeting.send_log}
        onExport={() => fetchMeetingMinutesMeetingPdf(id, meetingId)}
      />

      <PMDialog
        open={saveTemplateOpen}
        onClose={() => setSaveTemplateOpen(false)}
        title={t("meeting_minutes.templates.save_from_meeting")}
        size="md"
      >
        <div className="space-y-4">
          <input
            type="text"
            value={templateTitle}
            onChange={(e) => setTemplateTitle(e.target.value)}
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
            placeholder={t("meeting_minutes.title")}
          />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setSaveTemplateOpen(false)} className={mmSecondaryButtonClass()}>
              {t("common.cancel")}
            </button>
            <button type="button" onClick={handleSaveAsTemplate} className={mmPrimaryButtonClass()}>
              {t("common.save")}
            </button>
          </div>
        </div>
      </PMDialog>

      <PMDialog
        open={groupDialogOpen}
        onClose={closeGroupDialog}
        title={editingGroup ? t("meeting_minutes.point.edit") : t("meeting_minutes.add_point")}
        size="lg"
      >
        <div className="space-y-4">
          <input
            type="text"
            value={groupFormData.title}
            onChange={(e) => setGroupFormData({ ...groupFormData, title: e.target.value })}
            placeholder={t("meeting_minutes.point.title")}
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
            autoFocus
          />
          <textarea
            value={groupFormData.description}
            onChange={(e) => setGroupFormData({ ...groupFormData, description: e.target.value })}
            placeholder={t("meeting_minutes.point.description")}
            rows={4}
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
          />
          <div className="flex justify-end gap-3 border-t pt-4">
            <button type="button" onClick={closeGroupDialog} className={mmSecondaryButtonClass()}>
              {t("common.cancel")}
            </button>
            <button type="button" onClick={saveGroup} disabled={saving} className={mmPrimaryButtonClass()}>
              {saving ? t("common.saving") : editingGroup ? t("common.update") : t("common.create")}
            </button>
          </div>
        </div>
      </PMDialog>

      <PMDialog
        open={finishConfirmOpen}
        onClose={() => {
          if (!markingFinished) setFinishConfirmOpen(false);
        }}
        title={t("meeting_minutes.meeting.mark_finished_title")}
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">{t("meeting_minutes.meeting.mark_finished_body")}</p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setFinishConfirmOpen(false)}
              disabled={markingFinished}
              className={mmSecondaryButtonClass()}
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={confirmMarkFinished}
              disabled={markingFinished}
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              <CheckIcon className="h-4 w-4" />
              {t("meeting_minutes.meeting.mark_finished")}
            </button>
          </div>
        </div>
      </PMDialog>

      <HistoriesModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="meeting_minutes_meeting"
        module_param={meeting.id}
      />
    </div>
  );
};
